"use client"
import Image from "next/image"
import Link from "next/link"
import type { UserView } from "../lib/user-view.ts"
import { PointAmount } from "./point-amount.tsx"

type ProductViewModel = Extract<
  UserView,
  {
    readonly kind: "product"
  }
>
const isSupplierCode = (value: string): boolean => /^[A-Z0-9_]+$/.test(value) && value === value.toUpperCase()
const checkoutStatus = (
  view: ProductViewModel,
): {
  readonly label: string
  readonly tone: "available" | "unavailable" | "warning"
} | null => {
  if (view.checkoutReadiness === undefined) return null
  switch (view.checkoutReadiness.checkoutEligibility) {
    case "ELIGIBLE":
      return { label: "교환 준비 가능", tone: "available" }
    case "PHONE_VERIFICATION_REQUIRED":
      return { label: "휴대전화 확인 필요", tone: "warning" }
    case "PHONE_REVERIFICATION_REQUIRED":
      return { label: "휴대전화 다시 확인 필요", tone: "warning" }
    case "BLOCKED":
      return { label: "현재 교환 불가", tone: "unavailable" }
  }
}
export const RedesignProductDetail = ({ availableWon, product, recipient }: { readonly availableWon?: number; readonly product: ProductViewModel["product"]; readonly recipient?: string | null }) => {
  const category = product.category.length === 0 || isSupplierCode(product.category) ? null : product.category
  const remainingWon = availableWon === undefined ? undefined : availableWon - product.priceWon
  return (
    <section className="product-detail product-detail-redesign" data-product-detail={product.id}>
      <div className="redesign-product-hero">
        <Image alt={product.name} height={180} sizes="(max-width: 47.99rem) 180px, 240px" src={product.imageUrl} unoptimized width={180} />
      </div>
      <div className="redesign-product-body">
        <div className="redesign-product-intro">
          {category === null ? null : (
            <p className="redesign-product-category" data-product-field="category">
              {category}
            </p>
          )}
          <h2 data-product-field="name">{product.name}</h2>
          {!product.available ? (
            <p className="product-availability is-unavailable" data-product-field="availability">
              현재 판매 중지
            </p>
          ) : null}
        </div>
        <div className="redesign-product-amount">
          <span>사용 포인트</span>
          <strong data-product-field="price">
            <PointAmount amount={product.priceWon} />
          </strong>
        </div>
        <dl className="redesign-product-facts">
          <div>
            <dt>발송</dt>
            <dd>교환 완료 후 문자</dd>
          </div>
          {recipient === undefined ? null : (
            <div>
              <dt>받는 번호</dt>
              <dd>{recipient ?? "휴대전화 확인 필요"}</dd>
            </div>
          )}
          {remainingWon === undefined ? null : (
            <div>
              <dt>교환 후 잔액</dt>
              <dd data-product-remaining-balance>{remainingWon < 0 ? "포인트 부족" : <PointAmount amount={remainingWon} />}</dd>
            </div>
          )}
        </dl>
        <section className="redesign-product-notice">
          <h3>유의사항</h3>
          <ul>
            <li>상품 발급이 시작되면 취소와 수령번호 변경이 어려워요.</li>
            <li>휴대전화 번호와 사용 포인트를 확인한 뒤 진행해 주세요.</li>
            <li>상품별 유효기간과 이용 조건은 발급된 상품 안내를 확인해 주세요.</li>
          </ul>
        </section>
      </div>
    </section>
  )
}
export const ProductView = ({ view }: { readonly view: ProductViewModel }) => {
  const readiness = checkoutStatus(view)
  return (
    <>
      <RedesignProductDetail product={view.product} {...(view.checkoutReadiness === undefined ? {} : { recipient: view.checkoutReadiness.maskedRecipient })} />
      {readiness === null ? null : (
        <p className={`product-readiness is-${readiness.tone}`} data-checkout-eligibility={view.checkoutReadiness?.checkoutEligibility}>
          {readiness.label}
        </p>
      )}
      {view.checkoutReadiness?.checkoutEligibility === "PHONE_VERIFICATION_REQUIRED" || view.checkoutReadiness?.checkoutEligibility === "PHONE_REVERIFICATION_REQUIRED" ? (
        <Link className="button secondary" href="/my/phone">
          휴대전화 확인
        </Link>
      ) : null}
    </>
  )
}
